import { sessionPayload } from '../utils/session';
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Skeleton,
  Stack,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import HighlightOff from "@mui/icons-material/HighlightOff";
import PersonOutline from "@mui/icons-material/PersonOutline";

import { ROLE_PERMISSIONS } from "../utils/permissions/rolepermissions";
import React, { useEffect, useRef, useState } from "react";
import { fetchDrivers, fetchSchoolStaffReport, schoolReportScope } from "../redux/slices/driverSlice";
import { schoolViewPath } from "../utils/schoolStaff";
import { useReactToPrint } from "react-to-print";
import ENDPOINTS from "../utils/constants/endpoints";
import { post } from "../services/api";
import CommonDialog from "../components/CommonDialog";
import PrintableForm from "../components/printableForm";
import useIsMobile from "../hooks/useIsMobile";
import DocumentGallery from "../components/busDetails/DocumentGallery";
import ImageLightbox from "../components/busDetails/ImageLightbox";
import {
  backButtonStyle,
  cardStyle,
  contentStyle,
  fieldGrid,
  InfoField,
  pageStyle,
} from "../components/busDetails/detailLayout";
import {
  formatCertificateDate,
  parseCertificateDate,
  resolveFileUrl,
} from "../components/busDetails/documentUtils";

// Labels are "English / हिंदी"; backend values are shown once, untranslated.
const bi = (en, hi) => `${en} / ${hi}`;
const NOT_UPDATED = bi("Not updated", "उपलब्ध नहीं");
// Roboto/Lato lack Devanagari, so let the browser fall back to a proper Hindi face.
const FONT_STACK =
  '"Roboto", "Helvetica", "Arial", "Noto Sans Devanagari", "Nirmala UI", "Mangal", sans-serif';

const hasValue = (value) =>
  value != null && String(value).trim() !== "" && !/^(null|undefined)$/i.test(String(value).trim());
const showDate = (value) =>
  parseCertificateDate(value) ? formatCertificateDate(value) : value;

// Colour only; the chip always shows the backend's own status text.
export function statusTone(value) {
  const status = String(value ?? "").trim().toLowerCase();
  if (!status) return "default";
  if (/reject|disapprov|fail|declin|cancel|denied/.test(status)) return "error";
  if (/pend|unverif|not verif|process|wait|review|progress/.test(status)) return "warning";
  if (/approv|verif|success|complet|accept/.test(status)) return "success";
  return "default";
}

function SectionCard({ title, children, sx }) {
  return (
    <Card sx={{ ...cardStyle, height: "auto", ...sx }}>
      <CardContent sx={contentStyle}>
        <Typography component="h2" variant="subtitle1" fontWeight={700} lineHeight={1.3} mb={1}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function FieldGrid({ fields, size }) {
  return (
    <Grid container {...fieldGrid}>
      {fields.map(([label, value]) => (
        <Grid key={label} size={size}>
          <InfoField label={label} value={hasValue(value) ? String(value) : ""} empty={NOT_UPDATED} />
        </Grid>
      ))}
    </Grid>
  );
}

function StatusRow({ label, place, status, datetime }) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}
      sx={{ py: 1, "& + &": { borderTop: "1px solid #E5EAF2" } }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.3}>{label}</Typography>
        {hasValue(place) && (
          <Typography variant="body2" fontWeight={600} lineHeight={1.35} sx={{ mt: "2px", overflowWrap: "anywhere" }}>{place}</Typography>
        )}
        {hasValue(datetime) && (
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.35}>{showDate(datetime)}</Typography>
        )}
      </Box>
      <Chip size="small" variant="outlined" color={statusTone(status)}
        label={hasValue(status) ? status : bi("Not available", "उपलब्ध नहीं")} sx={{ flexShrink: 0, fontWeight: 600 }} />
    </Stack>
  );
}

function ProfilePhoto({ url, name }) {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const frame = { flexShrink: 0, width: 100, height: 125, borderRadius: 2, border: "1px solid #E5EAF2", bgcolor: "#F4F7FB", overflow: "hidden" };
  if (!url || failed) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={.25} sx={{ ...frame, borderStyle: "dashed" }} role="img" aria-label={bi("Photo not available", "फ़ोटो उपलब्ध नहीं")}>
        <PersonOutline sx={{ fontSize: 40, color: "#94A3B8" }} />
        <Typography variant="caption" color="text.secondary" textAlign="center" lineHeight={1.2}>No photo<br />फ़ोटो नहीं</Typography>
      </Stack>
    );
  }
  const label = `${name || "Driver"} photograph`;
  return (
    <>
      <Box component="button" type="button" onClick={() => setOpen(true)} aria-label={`Open full-size ${label}`}
        sx={{ ...frame, p: 0, display: "block", cursor: "zoom-in", "&:focus-visible": { outline: "2px solid #2563EB", outlineOffset: 2 } }}>
        <Box component="img" src={url} alt={label} onError={() => setFailed(true)} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </Box>
      <ImageLightbox open={open} onClose={() => setOpen(false)} images={[{ url, label }]} index={0} onError={() => setFailed(true)} />
    </>
  );
}

export default function DriverDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const schoolReturn = location.state?.schoolReturn;
  const returnToList = () => schoolReturn ? navigate(-1) : navigate("/drivers");
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  const dispatch = useDispatch();
  const drivers = useSelector((state) => state.drivers.list);
  const loading = useSelector((state) => state.drivers.loading);
  const scope = useSelector((state) => state.drivers.scope);

  const driver = drivers.find((d) => String(d.driver_id) === String(id));
  const printRef = useRef(null);

  const handlePrintWeb = useReactToPrint({
    documentTitle: `${driver?.contact_person || "Driver"}_Details_Form`,
    contentRef: printRef,
    onAfterPrint: () => console.log("Printing completed"),
  });
  const isMobile = useIsMobile();
  const handlePrint = () => {
    if (isMobile) {
      if (printRef.current) {
        localStorage.setItem("printContent", printRef.current.innerHTML);
        navigate("/print");
      }
    } else {
      handlePrintWeb();
    }
  };
  const handleApprove = async (driver_id) => {
    try {
      const payload = {
        token: localStorage.getItem("token"),
        users_id: sessionPayload().users_id,
        role_id: sessionPayload().role_id,
        driver_id,
      };
      const res = await post(ENDPOINTS.DRIVER.APPROVE, payload);

      if (res?.status === "success") {
        setDialog({
          open: true,
          title: "Success",
          message: "Driver Approved Successfully",
          type: "success",
        });
      } else {
        setDialog({
          open: true,
          title: "Error",
          message: res?.["success message"] || "Failed to approve driver",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Approve error:", err);
      setDialog({
        open: true,
        title: "Error",
        message: "Something went wrong while approving.",
        type: "error",
      });
    }
  };

  const handleCloseDialog = () => {
    setDialog({ ...dialog, open: false });
    returnToList(); // 👈 go back after closing
  };

  const handleDisapprove = async (driver_id) => {
    const reason = prompt("Enter reason for disapproval:");
    if (!reason) return;

    try {
      const payload = {
        token: localStorage.getItem("token"),
        users_id: sessionPayload().users_id,
        role_id: sessionPayload().role_id,
        driver_id,
        reason,
      };
      const res = await post(ENDPOINTS.DRIVER.DISAPPROVE, payload);

      if (res?.status === "success") {
        setDialog({
          open: true,
          title: "Success",
          message: "Driver Disapproved Successfully",
          type: "success",
        });
      } else {
        setDialog({
          open: true,
          title: "Error",
          message: res?.["success message"] || "Failed to disapprove driver",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Reject error:", err);
      setDialog({
        open: true,
        title: "Error",
        message: "Something went wrong while disapproving.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    // Opened from School Details, the person belongs to that school's staff report, not the MIS account's listing.
    const schoolId = schoolReturn?.school?.school_id;
    if (schoolId != null && String(schoolId).trim() !== "") {
      if (scope !== schoolReportScope(schoolId)) dispatch(fetchSchoolStaffReport({ schoolId: String(schoolId) }));
    } else if (!drivers.length) {
      dispatch(fetchDrivers());
    }
    // Fetch once on open; an empty successful response must not trigger a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const { role_id } = useSelector((state) => state.auth);
  const permission = ROLE_PERMISSIONS[role_id] || {};

  const backButton = (
    <Button component={Link} to={schoolReturn ? schoolViewPath(schoolReturn.school) : "/drivers"} state={schoolReturn} onClick={schoolReturn ? (event) => { event.preventDefault(); returnToList(); } : undefined} variant="text" size="small" startIcon={<ArrowBackRounded sx={{ fontSize: 18 }} />} sx={backButtonStyle}>
      {schoolReturn ? "Back to School Details" : bi("Back to Driver/Conductor List", "चालक/परिचालक सूची")}
    </Button>
  );
  const page = { ...pageStyle, "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-label": { fontFamily: FONT_STACK } };

  if (!driver) {
    return (
      <Box sx={page}>
        {backButton}
        {loading ? (
          <Box role="status" aria-label="Loading driver details">
            <Skeleton variant="text" width={220} height={40} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={150} sx={{ mb: 1.5 }} />
            <Skeleton variant="rounded" height={140} sx={{ mb: 1.5 }} />
            <Skeleton variant="rounded" height={260} />
          </Box>
        ) : (
          <Alert severity="warning">{bi("Driver/Conductor not found", "चालक/परिचालक नहीं मिला")}</Alert>
        )}
      </Box>
    );
  }

  const formatAddress = (prefix) =>
    [
      driver[`${prefix}_address1`],
      driver[`${prefix}_address2`],
      driver[`${prefix}_address3`],
      driver[`${prefix}_city`],
      driver[`${prefix}_state`],
      driver[`${prefix}_country`],
      driver[`${prefix}_zip_code`],
    ]
      .filter(hasValue)
      .join(", ");

  const role = driver.firm_types || driver.designation;
  const experience = [
    hasValue(driver.experience_year) && `${driver.experience_year} yr`,
    hasValue(driver.experience_month) && `${driver.experience_month} mo`,
  ].filter(Boolean).join(" ");
  const isPending = driver.police_station_level1_status === "Pending";
  const canEditOrDelete = (role_id === "Mg==" || role_id === "Nw==") && permission.canEdit && isPending;

  const profileFields = [
    [bi("Full Name", "पूरा नाम"), driver.contact_person],
    [bi("Role", "पद"), role],
    [bi("Registration Number", "पंजीकरण संख्या"), driver.register_no],
    [bi("Mobile Number", "मोबाइल नंबर"), driver.mobile1],
    [bi("School Name", "विद्यालय का नाम"), driver.school],
  ];
  // Optional fields only appear when the backend actually supplied them.
  const optional = (label, value) => (hasValue(value) ? [[label, value]] : []);
  const isAttendant = String(driver.firm_types || "").trim().toLowerCase() === "attendant";
  const generalFields = [
    ...optional(bi("Father/Husband Name", "पिता/पति का नाम"), driver.father_name),
    [bi("Date of Birth", "जन्म तिथि"), showDate(driver.date_birth)],
    ...(!isAttendant || hasValue(driver.dl_no) ? [[bi("Driving Licence Number", "ड्राइविंग लाइसेंस संख्या"), driver.dl_no],
    [bi("Driving Licence Type", "ड्राइविंग लाइसेंस का प्रकार"), driver.dl_type],
    [bi("DL RTO", "लाइसेंस जारी करने वाला आरटीओ"), driver.dl_rto_name]] : []),
    ...optional(bi("DL Issue Date", "लाइसेंस जारी तिथि"), showDate(driver.dl_issue_date)),
    ...optional(bi("DL Renewal Date", "लाइसेंस नवीनीकरण तिथि"), showDate(driver.dl_renewal_date)),
    [bi("Experience", "अनुभव"), experience],
    ...optional(bi("Alternate Mobile", "वैकल्पिक मोबाइल"), driver.mobile2),
    [bi("Email", "ईमेल"), driver.email],
    [bi("School", "विद्यालय"), driver.school],
  ];
  const addressFields = [
    [bi("Present Address", "वर्तमान पता"), formatAddress("present")],
    [bi("Permanent Address", "स्थायी पता"), formatAddress("permanent")],
    [bi("Last 5 Years Address", "पिछले 5 वर्षों का पता"), formatAddress("last5year")],
  ];
  const documents = [
    { key: "aadhaar", label: bi("Aadhaar Card", "आधार कार्ड"), path: driver.upload_aadhaar, meta: hasValue(driver.aadhaar_no) ? `${bi("Aadhaar No.", "आधार संख्या")}: ${driver.aadhaar_no}` : null },
    ...(!isAttendant || hasValue(driver.upload_dl) ? [{ key: "dl", label: bi("Driving Licence", "ड्राइविंग लाइसेंस"), path: driver.upload_dl, meta: hasValue(driver.dl_no) ? `${bi("DL No.", "लाइसेंस संख्या")}: ${driver.dl_no}` : null }] : []),
  ];
  const actionButton = { borderRadius: "8px", px: 1.25, minHeight: { xs: 40, sm: 32 }, fontSize: 13, whiteSpace: "nowrap" };

  return (
    <Box sx={page}>
      {backButton}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={1} mb={1.25}>
        <Typography component="h1" variant="h5" fontWeight={800} sx={{ fontSize: { xs: 21, sm: 24, md: 26 }, lineHeight: 1.2, overflowWrap: "anywhere", color: "#102A43", minWidth: 0 }}>
          {driver.contact_person || NOT_UPDATED}
        </Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ flexShrink: 0 }}>
          {canEditOrDelete && (
            <>
              <Button variant="outlined" size="small" startIcon={<EditOutlined />} sx={actionButton}
                onClick={() => navigate(`/drivers/edit/${driver.driver_id}`)}>
                {bi("Edit", "संपादित करें")}
              </Button>
              <Button variant="outlined" size="small" startIcon={<DeleteOutline />} sx={{ ...actionButton, color: "#DC2626 !important", borderColor: "#FCA5A5" }}
                onClick={() => window.confirm("Delete this driver?")}>
                {bi("Delete", "हटाएँ")}
              </Button>
            </>
          )}
          {permission.canApprove && isPending && (
            <Button variant="outlined" size="small" startIcon={<CheckCircleOutline />} sx={{ ...actionButton, color: "#15803D !important", borderColor: "#86EFAC" }}
              onClick={() => handleApprove(driver.driver_id)}>
              {bi("Approve", "स्वीकृत करें")}
            </Button>
          )}
          {permission.canReject && isPending && (
            <Button variant="outlined" size="small" startIcon={<HighlightOff />} sx={{ ...actionButton, color: "#DC2626 !important", borderColor: "#FCA5A5" }}
              onClick={() => handleDisapprove(driver.driver_id)}>
              {bi("Reject", "अस्वीकार करें")}
            </Button>
          )}
          <Button variant="contained" size="small" disableElevation startIcon={<PrintOutlined />} sx={{ ...actionButton, color: "#fff !important", bgcolor: "#2563EB" }}
            onClick={handlePrint}>
            {bi("Print", "प्रिंट")}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={1.5}>
        {/* The short verification card sits beside the equally short profile card, so neither leaves blank space. */}
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <Card sx={cardStyle}>
            <CardContent sx={contentStyle}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                <ProfilePhoto url={resolveFileUrl(driver.upload_photo)} name={driver.contact_person} />
                <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                  <FieldGrid fields={profileFields} size={{ xs: 12, sm: 6, lg: 4 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          <SectionCard title={bi("Verification Status", "सत्यापन स्थिति")} sx={{ height: "100%" }}>
            <Box sx={{ mt: -1 }}>
              <StatusRow label={bi("School", "विद्यालय")} place={driver.school} status={driver.school_level1_status} datetime={driver.school_level1_datetime} />
              <StatusRow label={bi("Police Station", "पुलिस थाना")} place={driver.police_station || driver.police_station_name} status={driver.police_station_level1_status} datetime={driver.police_station_level1_datetime} />
            </Box>
          </SectionCard>
        </Grid>

        <Grid size={12}>
          <SectionCard title={bi("General Information", "सामान्य जानकारी")}>
            <FieldGrid fields={generalFields} size={{ xs: 12, sm: 6, md: 4, lg: 3 }} />
          </SectionCard>
        </Grid>

        <Grid size={12}>
          <SectionCard title={bi("Address Details", "पता विवरण")}>
            <FieldGrid fields={addressFields} size={{ xs: 12, sm: 6, lg: 4 }} />
          </SectionCard>
        </Grid>

        <Grid size={12}>
          <SectionCard title={bi("Documents", "दस्तावेज़")}>
            <DocumentGallery
              documents={documents}
              itemName="document"
              text={{
                missing: bi("Not uploaded", "अपलोड नहीं"),
                broken: bi("Image unavailable", "छवि उपलब्ध नहीं"),
                image: bi("Image", "छवि"),
                pdf: bi("PDF document", "पीडीएफ दस्तावेज़"),
                word: bi("Word document", "वर्ड दस्तावेज़"),
                spreadsheet: bi("Spreadsheet", "स्प्रेडशीट"),
                file: bi("File attachment", "फ़ाइल"),
                enlarge: bi("Click the preview to enlarge", "बड़ा देखने के लिए क्लिक करें"),
                uploaded: (count, total) => bi(`${count} of ${total} uploaded`, `${total} में से ${count} अपलोड`),
              }}
              actionLabels={{
                download: bi("Download", "डाउनलोड"),
                viewPdf: bi("View PDF", "पीडीएफ देखें"),
                preview: bi("Preview", "पूर्वावलोकन"),
                close: bi("Close", "बंद करें"),
              }}
            />
          </SectionCard>
        </Grid>
      </Grid>

      <div style={{ position: "absolute", top: "-10000px" }}>
        <PrintableForm ref={printRef} driver={driver} />
      </div>
      <CommonDialog
        open={dialog.open}
        onClose={handleCloseDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />
    </Box>
  );
}
